import React, { useState, useEffect, useRef } from 'react';
import './AppConcept.css';
import { generateMenuPDF } from './utils/pdfGenerator';
import { useScrollAnimation } from './hooks/useScrollAnimation';
import BurgerMenu from './components/BurgerMenu';
import Lightbox from './components/Lightbox';
import {
  API_URL,
  FALLBACK_MENU_ITEMS,
  MENU_ITEM_NAMES
} from './constants';

/**
 * Concept Version - Art Gallery Aesthetic
 *
 * Radical redesign with modern artist/gallery aesthetic
 * Bold typography, asymmetric layouts, cutting-edge effects
 *
 * @component
 * @returns {JSX.Element} Concept Middle Street Coffee website
 */
const GALLERY_IMAGES = [
  { src: '/images/gallery-espresso.jpg', fullSrc: '/images/gallery-espresso-full.jpg', alt: 'Espresso extraction into a steel pitcher' },
  { src: '/images/gallery-latte-art.jpg', fullSrc: '/images/gallery-latte-art-full.jpg', alt: 'Barista pouring latte art into a ceramic cup' },
  { src: '/images/gallery-baked-goods.jpg', fullSrc: '/images/gallery-baked-goods-full.jpg', alt: 'Counter display of fresh croissants, brownies and cakes' },
  { src: '/images/gallery-window.jpg', fullSrc: '/images/gallery-window-full.jpg', alt: 'Lattes and a croissant on the window ledge' },
  { src: '/images/gallery-interior.jpg', fullSrc: '/images/gallery-interior-full.jpg', alt: 'Interior shelving with coffee bags, merch and art' },
  { src: '/images/gallery-latte-cake.jpg', fullSrc: '/images/gallery-latte-cake-full.jpg', alt: 'Latte and cake by the window with a fern plant' },
];

function AppConcept() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const headerRef = useRef(null);
  const heroTitleRef = useRef(null);

  // Initialize scroll animations
  useScrollAnimation();

  // Morphing header effect - hide hero title, show header
  useEffect(() => {
    let ticking = false;
    const threshold = 300; // Scroll threshold for morph

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;

          if (headerRef.current) {
            if (scrollY > threshold) {
              headerRef.current.classList.add('scrolled');
            } else {
              headerRef.current.classList.remove('scrolled');
            }
          }

          if (heroTitleRef.current) {
            if (scrollY > threshold) {
              heroTitleRef.current.classList.add('hide-on-scroll');
            } else {
              heroTitleRef.current.classList.remove('hide-on-scroll');
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch menu prices from ordercoffee.uk API
  useEffect(() => {
    const fetchMenuPrices = async () => {
      try {
        const response = await fetch(API_URL);

        // Check HTTP status code
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.products) {
          // Transform products into menu categories
          const coffee = data.products
            .filter(p => MENU_ITEM_NAMES.includes(p.name))
            .map(p => ({
              name: p.name,
              price: `£${(p.price / 100).toFixed(2)}`
            }));

          setMenuItems(coffee);
        } else {
          // API returned but with unsuccessful response
          setMenuItems(FALLBACK_MENU_ITEMS);
        }
      } catch (error) {
        // Log error only in development
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching menu:', error);
        }
        // Use fallback prices
        setMenuItems(FALLBACK_MENU_ITEMS);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuPrices();
  }, []);

  // Deep links like /#menu (e.g. the Google Business Profile menu link): the
  // browser resolves the fragment against the static pre-render, which React
  // replaces on mount — iOS in-app webviews then never re-scroll. Re-anchor
  // after mount, and again once menu items load and shift the layout.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const scrollToHash = () => {
      const el = document.getElementById(id);
      if (!el) return;
      // Must jump instantly: the root has scroll-behavior smooth, and smooth
      // programmatic scrolls get cancelled by image-load layout shifts.
      const html = document.documentElement;
      const prev = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      window.scrollTo(0, el.getBoundingClientRect().top + window.pageYOffset);
      html.style.scrollBehavior = prev;
    };
    // Images above the menu keep shifting layout after first paint, so
    // re-anchor at each settling point: next frame, full load, and a fallback.
    requestAnimationFrame(scrollToHash);
    window.addEventListener('load', scrollToHash, { once: true });
    const t = setTimeout(scrollToHash, 1000);
    return () => {
      window.removeEventListener('load', scrollToHash);
      clearTimeout(t);
    };
  }, [menuItems]);

  return (
    <div className="App">
      {/* Burger Menu - Navigation */}
      <BurgerMenu />

      {/* Header */}
      <header ref={headerRef} className="App-header" role="banner">
        <div className="header-content">
          <h1>MIDDLE STREET COFFEE</h1>
        </div>
      </header>

      {/* Main Content */}
      <main role="main">
        {/* Hero Section with Background Image */}
        <section id="hero" className="hero" style={{ backgroundImage: 'url(/images/hero-exterior.jpg)' }}>
        <div className="hero-content">
          <div className="hero-text-box" ref={heroTitleRef}>
            <h2>Middle Street Coffee</h2>
            <div className="hero-subtitle">Speciality</div>
            <div className="hero-subtitle">Coffee in Deal</div>
          </div>
          <div className="hero-text-box">
            <p>Order ahead online to skip the wait, or order at the counter when you arrive.</p>
          </div>
          <a href="https://ordercoffee.uk" className="hero-cta" target="_blank" rel="noopener noreferrer">
            Order Online
          </a>
        </div>
      </section>

      {/* About Section with Photo */}
      <section id="about" className="section white-bg">
        <div className="container">
          <p className="section-subtitle">Established November 2023</p>

          <div className="about-with-photo">
            <div className="about-photo" style={{ backgroundImage: 'url(/images/about-interior.jpg)' }}>
            </div>

            <div className="about-content">
              <p>
                Since opening, we've been serving specialty coffee in our compact, laid-back space. We are passionate about coffee and aim to make each one as good as it can be.
              </p>
              <p>
                Our customers especially love our oat milk lattes, friendly atmosphere and the fact that we welcome dogs - there are always treats at the counter for your four-legged friends!
              </p>
              <p>
                To make life easier for those on the go, we offer online ordering through <strong style={{color: 'var(--green-primary)'}}>ordercoffee.uk</strong>. Order your coffee for collection straight away or schedule it for later in the day. Perfect for morning commutes or lunch breaks when you're short on time. Your order will be ready and waiting when you arrive.
              </p>
              <p>
                We're proudly independent, currently working with Assembly of London to bring our customers high quality coffee without any sort of pretension. We partner with roasters who provide consistent, high quality beans, and our relationships evolve as we seek out the best specialty coffee for our customers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section id="approach" className="section alt-bg">
        <div className="container">
          <p className="section-subtitle">Our Approach</p>

          <div className="pillars">
            <div className="pillar animate-on-scroll">
              <h3>Quality Coffee</h3>
              <p>
                Our baristas are passionate about coffee and work hard to make each drink right. Want a flat white or curious about our latest roast? We're here to help.
              </p>
            </div>

            <div className="pillar animate-on-scroll">
              <h3>Dog Friendly</h3>
              <p>
                Bring your furry friend. We love dogs and keep treats at the counter. Our cozy space welcomes well behaved pups alongside their coffee loving humans.
              </p>
            </div>

            <div className="pillar animate-on-scroll">
              <h3>Community Focused</h3>
              <p>
                Since 2023, we've been a welcoming spot for locals and visitors. Laid back atmosphere, quality coffee, and a friendly team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Information */}
      <section className="section white-bg">
        <div className="container">
          <p className="section-subtitle">Good to Know</p>

          <div className="pillars">
            <div className="pillar animate-on-scroll">
              <h3>Seating</h3>
              <p>
                There are seats for approximately 14 people in our compact, cozy space. Perfect for a quick coffee or intimate catch-up.
              </p>
            </div>

            <div className="pillar animate-on-scroll">
              <h3>Parking</h3>
              <p>
                Convenient public parking at Middle Street Car Park and South Street Car Park, both operated by Dover District Council. Just a short walk from our door.
              </p>
            </div>

            <div className="pillar animate-on-scroll">
              <h3>Perfect Location</h3>
              <p>
                Right in the heart of Deal. Just minutes from Deal Pier, the high street, and the seafront. Deal station is a short walk away, making us ideal for visitors and commuters alike.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section - PDF Style */}
      <section id="menu" className="section white-bg">
        <div className="container">
          <div className="menu-section">
            <div className="menu-header">
              <h2>Our Menu</h2>
              <p style={{color: 'var(--light-gray)', fontStyle: 'italic', fontSize: '0.9rem', fontWeight: '300'}}>All drinks available hot or iced</p>
            </div>

            {/* Coffee */}
            <div className="menu-category">
              <h3>Specialty Coffee</h3>
              <div className="menu-items">
                {loading ? (
                  <p className="loading">Loading menu...</p>
                ) : (
                  menuItems.map((item, index) => (
                    <div key={index} className="menu-item-row">
                      <span className="menu-item-name">{item.name}</span>
                      <span className="menu-item-dots"></span>
                      <span className="menu-item-price">{item.price}</span>
                    </div>
                  ))
                )}
              </div>
              <span className="menu-note" role="note">Try our customer-favorite Oat Milk Latte!</span>
              <span className="menu-note" role="note">Milk alternatives available: Oat, Almond, Soy, Coconut, Pea (no extra charge)</span>
              <span className="menu-note" role="note">Add a flavoured syrup (+60p)</span>
              <span className="menu-note" role="note" style={{marginTop: '1.5rem'}}>Allergens: All items are vegetarian. Some items are vegan. Our suppliers and baked goods vary daily. Please ask staff at time of order for current allergen information.</span>
            </div>

            {/* Tea */}
            <div className="menu-category">
              <h3>Tea Selection</h3>
              <div className="menu-items">
                <div className="menu-item-row">
                  <span className="menu-item-name">English Breakfast</span>
                  <span className="menu-item-dots"></span>
                  <span className="menu-item-price">£2.80</span>
                </div>
                <div className="menu-item-row">
                  <span className="menu-item-name">Earl Grey</span>
                  <span className="menu-item-dots"></span>
                  <span className="menu-item-price">£2.80</span>
                </div>
                <div className="menu-item-row">
                  <span className="menu-item-name">Green Tea</span>
                  <span className="menu-item-dots"></span>
                  <span className="menu-item-price">£2.80</span>
                </div>
                <div className="menu-item-row">
                  <span className="menu-item-name">Herbal Tea</span>
                  <span className="menu-item-dots"></span>
                  <span className="menu-item-price">£2.80</span>
                </div>
              </div>
            </div>

            {/* Extras */}
            <div className="menu-category">
              <h3>Extras</h3>
              <div className="menu-items">
                <div className="menu-item-row">
                  <span className="menu-item-name">Baked Goods</span>
                  <span className="menu-item-dots"></span>
                  <span className="menu-item-price">From £2.50</span>
                </div>
              </div>
              <span className="menu-note" role="note">Ask about our seasonal specials and daily bakes</span>
            </div>

            <div className="menu-footer">
              <button className="menu-pdf-link" onClick={() => {
                generateMenuPDF(menuItems);
              }}>
                Download Full Menu (PDF)
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section id="gallery" className="section alt-bg">
        <div className="container">
          <p className="section-subtitle">A Glimpse Inside</p>

          <div className="gallery">
            {GALLERY_IMAGES.map((image, index) => (
              <div
                key={index}
                className="gallery-item animate-on-scroll"
                onClick={() => setLightboxIndex(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightboxIndex(index); } }}
                aria-label={`View larger: ${image.alt}`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  width="800"
                  height="800"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          <p style={{textAlign: 'center', marginTop: '2rem', color: 'var(--gray)'}}>
            Follow us on <a href="https://instagram.com/middlest.coffee" target="_blank" rel="noopener noreferrer" style={{color: 'var(--green-primary)', fontWeight: '500'}}>@middlest.coffee</a> for more
          </p>
        </div>
      </section>

      {/* Order Online CTA */}
      <section className="order-cta-section">
        <h2>Order Ahead</h2>
        <p>Save time by ordering online. Choose immediate collection or schedule for later. Your coffee will be ready when you arrive.</p>
        <a href="https://ordercoffee.uk" className="order-btn" target="_blank" rel="noopener noreferrer">
          Order Now
        </a>
      </section>

      {/* Location & Contact with Map */}
      <section id="location" className="section white-bg">
        <div className="container">
          <p className="section-subtitle">Open Daily from 6:30am</p>

          <div className="location-contact">
            <div className="contact-info">
              <h3>Find Us</h3>
              <p><strong>Address</strong><br />
                10 Broad Street<br />
                Deal, Kent<br />
                CT14 6ER
              </p>

              <p style={{marginTop: '2rem'}}><strong>Hours</strong><br />
                Sunday - Thursday: 6:30am - 4:00pm<br />
                Friday - Saturday: 6:30am - 9:00pm
              </p>

              <p style={{marginTop: '2rem'}}><strong>Get In Touch</strong><br />
                Phone: <a href="tel:01304732757">01304 732 757</a><br />
                Email: <a href="mailto:hello@middlestcoffee.shop">hello@middlestcoffee.shop</a><br />
                <a href="https://api.whatsapp.com/message/LNBSWB7KTQVBG1" target="_blank" rel="noopener noreferrer">WhatsApp Us</a>
              </p>
            </div>

            <div className="map-container">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d156.1742505709786!2d1.4035551130970794!3d51.222980763499805!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47deaf84c7a3a1f1%3A0x6e0a667fe5136159!2sMiddle%20Street%20Coffee!5e0!3m2!1sen!2suk!4v1763935748255!5m2!1sen!2suk"
                width="600"
                height="450"
                style={{border:0}}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                sandbox="allow-scripts allow-same-origin allow-popups"
                title="Middle Street Coffee Location"
              ></iframe>
            </div>
          </div>
        </div>
      </section>
      </main>

      {lightboxIndex !== null && (
        <Lightbox
          images={GALLERY_IMAGES}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}

      {/* Footer */}
      <footer className="app-footer" role="contentinfo">
        <div className="footer-content">
          <div className="social-links">
            <a href="https://instagram.com/middlest.coffee" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="white"/>
              </svg>
            </a>
            <a href="https://api.whatsapp.com/message/LNBSWB7KTQVBG1" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" fill="white"/>
              </svg>
            </a>
          </div>

          <div className="footer-bottom">
            <div className="footer-links">
              <a href="/privacy-policy.html">Privacy</a>
              <span style={{color: 'rgba(255,255,255,0.7)', margin: '0 0.5rem'}}>·</span>
              <a href="/cookie-policy.html">Cookies</a>
              <span style={{color: 'rgba(255,255,255,0.7)', margin: '0 0.5rem'}}>·</span>
              <a href="/accessibility.html">Accessibility</a>
            </div>
            <p>&copy; 2025 Middle Street Coffee · Deal, Kent</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default AppConcept;
